import * as Sentry from '@sentry/react';

let initialized = false;

export function isSentryEnabled(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  return Boolean(dsn && import.meta.env.PROD);
}

export function initSentry(): void {
  if (initialized || !isSentryEnabled()) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN!.trim();
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE || 'production';

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 0.1),
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers.authorization;
        delete event.request.headers.cookie;
      }
      return event;
    },
  });

  Sentry.setTag('app_mode', import.meta.env.VITE_APP_MODE ?? 'tb-site');
  initialized = true;
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  if (context) {
    Sentry.withScope((scope) => {
      for (const [key, value] of Object.entries(context)) {
        if (value != null) scope.setContext(key, { value });
      }
      Sentry.captureException(error);
    });
    return;
  }
  Sentry.captureException(error);
}

export function setSentryUserContext(input: {
  id?: string | null;
  email?: string | null;
  role?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
  organizationSlug?: string | null;
  plan?: string | null;
}): void {
  if (!initialized) return;

  if (input.id) {
    Sentry.setUser({
      id: input.id,
      email: input.email ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }

  if (input.organizationId) {
    Sentry.setTag('organization_id', input.organizationId);
  }
  if (input.organizationName) {
    Sentry.setTag('organization_name', input.organizationName);
  }
  if (input.organizationSlug) {
    Sentry.setTag('organization_slug', input.organizationSlug);
  }
  if (input.role) {
    Sentry.setTag('user_role', input.role);
  }
  if (input.plan) {
    Sentry.setTag('org_status', input.plan);
    Sentry.setTag('plan', input.plan);
  }
}

export function setSentryRouteTag(pathname: string): void {
  if (!initialized) return;
  Sentry.setTag('route', pathname);
}
